import React, {
  ChangeEvent,
  DragEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import { VStack } from "@UI/Components/Stack/Stack";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Icon } from "@UI/Components/Icon/Icon";
import { Spinner } from "@UI/Components/Spinner/Spinner";
import { Box } from "@UI/Components/Box/Box";

import { Images } from "@UI/Components/Icons/Images";
import {
  usePhotoBookUpload,
  ALLOWED_PHOTO_EXTENSIONS,
} from "@modules/ServiceProviders/hooks/photoBook/usePhotoBookUpload";
import { IPhoto } from "../../Containers/PhotoBookRouter/Router";
import { ProgressCircle } from "@UI/Components/ProgressCircle/ProgressCircle";
import { ToastProps } from "@UI/hooks/useToast";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "../messages";
import i18n from "@I18n/services/i18n";

type IUploadBoxVariant = "small" | "big";
export interface IPhotoUploadBoxProps {
  variant?: IUploadBoxVariant;
  photoPreview: IPhoto[];
  setPhotoPreview: React.Dispatch<React.SetStateAction<IPhoto[]>>;
  showToast: (toastData: Pick<ToastProps, "status" | "description">) => void;
  incrementPagination: () => void;
}

export const PhotoUploadBox = ({
  variant = "big" as IUploadBoxVariant,
  setPhotoPreview,
  showToast,
  incrementPagination,
}: IPhotoUploadBoxProps) => {
  const ref = useRef<HTMLInputElement>(null);

  const [isImageDragging, setIsImageDragging] = useState(false);
  const [newImagesToUpload, setNewImagesToUpload] = useState<IPhoto>();

  const {
    uploadFile,
    uploadedData,
    isLoading,
    uploadProgress,
    isUploadSuccess,
    isUploadError,
  } = usePhotoBookUpload(newImagesToUpload, showToast);

  useEffect(() => {
    newImagesToUpload && uploadFile();
  }, [newImagesToUpload]);

  useEffect(() => {
    if (isUploadSuccess && newImagesToUpload) {
      setPhotoPreview((prev) =>
        prev
          ? [{ ...newImagesToUpload, id: uploadedData?.id }, ...prev]
          : [newImagesToUpload]
      );
      showToast({
        status: "success",
        description: i18n.formatMessage(messages.photoBookUploadSuccessMessage),
      });
      incrementPagination();
    }
  }, [isUploadSuccess]);

  useEffect(() => {
    isUploadError &&
      showToast({
        status: "error",
        description: i18n.formatMessage(messages.photoBookUploadErrorMessage),
      });
  }, [isUploadError]);

  const handleClick = useCallback((e) => {
    ref.current?.click();
    e.stopPropagation();
  }, []);

  const onImageAdd = useCallback((files: FileList) => {
    const validFiles = Array.from(files).filter((file) => !!file);

    const newImagePayload: IPhoto[] = [];
    validFiles &&
      validFiles.forEach((file) => {
        newImagePayload.push({
          id: uuidv4(),
          blobUrl: URL.createObjectURL(file),
          file_extension: file.name.split(".").pop(),
          rawFile: file,
        });
      });

    setNewImagesToUpload(newImagePayload[0]);
  }, []);

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.target.files && onImageAdd(event.target.files);
      event.target.value = "";
    },
    [onImageAdd]
  );

  const onImageDrag = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setIsImageDragging(true);
    } else if (event.type === "dragleave") {
      setIsImageDragging(false);
    }
  }, []);

  const onImageDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsImageDragging(false);
      onImageAdd(event.dataTransfer.files);
    },
    [onImageAdd]
  );

  return (
    <Box position="relative" cursor="pointer" onClick={handleClick}>
      <input
        ref={ref}
        // multiple
        type="file"
        hidden
        accept={ALLOWED_PHOTO_EXTENSIONS.map((ext) => `.${ext}`).join(",")}
        onChange={handleChange}
      />

      <VStack
        justifyContent="center"
        spacing="16px"
        py="40px"
        px="16px"
        sx={{
          "background-image": `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='12' ry='12' stroke='%23DEDEDEFF' stroke-width='4' stroke-dasharray='10' stroke-dashoffset='67' stroke-linecap='butt'/%3e%3c/svg%3e")`,
        }}
        borderRadius="10px"
        onDragEnter={onImageDrag}
        {...(variant === "small"
          ? {
              boxSize: { base: "140px", sm: "160px", md: "165px", lg: "236px" },
            }
          : { my: { base: "20px", lg: "32px" } })}
      >
        {isLoading ? (
          <>
            {uploadProgress != null && Number(uploadProgress) < 100 ? (
              <ProgressCircle progress={uploadProgress} size={"2.5rem"} />
            ) : (
              <Spinner color="gray.500" size="lg" />
            )}
          </>
        ) : variant === "big" ? (
          <>
            <Icon as={Images} height="26" width="26" color="gray.500" />
            <Text textAlign="center">
              Drag and drop or select your photos (.jpg, .jpeg, .png, .heic) -
              Max 10 Mb.
            </Text>
            <Button
              size="lg"
              type="submit"
              colorScheme="seaGreen"
              onClick={handleClick}
            >
              {/* {formatMessage(messages.connect)} */}
              Browse
            </Button>
          </>
        ) : (
          <VStack spacing="12px">
            <Icon
              as={Images}
              height="28px"
              width="28px"
              color="gray.500"
              cursor="pointer"
              onClick={handleClick}
            />
            <Text size="normal" textAlign="center">
              <FormattedMessage {...messages.photoBookAddPhoto} />
            </Text>
          </VStack>
        )}
      </VStack>
      {isImageDragging && (
        <Box
          background="#090D2899"
          position="absolute"
          width="100%"
          height="100%"
          borderRadius="2px"
          top="0px"
          right="0px"
          bottom="0px"
          left="0px"
          onDragEnter={onImageDrag}
          onDragLeave={onImageDrag}
          onDragOver={onImageDrag}
          onDrop={onImageDrop}
        ></Box>
      )}
    </Box>
  );
};
